import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Users, FileText, Image, Trophy, CheckSquare, 
  Ticket, Flag, Settings, Activity, ChevronLeft, Search, MoreVertical,
  Ban, Check, Eye, Trash2, Edit, Award, DollarSign, Shield, Star,
  ArrowUpRight, ArrowDownRight, TrendingUp, Clock, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Dashboard Overview Component
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur de chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
    </div>;
  }

  const statCards = [
    { label: 'Utilisateurs', value: stats?.users?.total || 0, icon: Users, color: 'blue', change: `+${stats?.users?.new_today || 0} aujourd'hui` },
    { label: 'Créateurs', value: stats?.users?.creators || 0, icon: Star, color: 'yellow' },
    { label: 'Posts', value: stats?.content?.total_posts || 0, icon: FileText, color: 'green', change: `+${stats?.content?.posts_today || 0} aujourd'hui` },
    { label: 'Stories', value: stats?.content?.total_stories || 0, icon: Image, color: 'purple' },
    { label: 'Défis Actifs', value: stats?.challenges?.active || 0, icon: Trophy, color: 'orange' },
    { label: 'Validations', value: stats?.moderation?.pending_validations || 0, icon: CheckSquare, color: 'red', alert: true },
    { label: 'Tickets Ouverts', value: stats?.moderation?.open_tickets || 0, icon: Ticket, color: 'gray' },
    { label: 'Signalements', value: stats?.moderation?.pending_reports || 0, icon: Flag, color: 'red', alert: true },
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-fraunces text-2xl font-bold text-gray-900">Dashboard Admin</h1>
        <Button onClick={fetchDashboard} variant="outline" size="sm">
          <Activity className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className={`bg-white rounded-xl p-4 border ${stat.alert && stat.value > 0 ? 'border-red-200 bg-red-50/50' : 'border-gray-100'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <span className="text-sm text-gray-500">{stat.label}</span>
            </div>
            <div className="flex items-end justify-between">
              <span className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</span>
              {stat.change && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" />
                  {stat.change}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Actions Requises
          </h3>
          <div className="space-y-3">
            {stats?.moderation?.pending_validations > 0 && (
              <NavLink to="/admin/validations" className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                <span className="text-sm font-medium text-orange-700">
                  {stats.moderation.pending_validations} validations en attente
                </span>
                <ArrowUpRight className="w-4 h-4 text-orange-500" />
              </NavLink>
            )}
            {stats?.moderation?.pending_reports > 0 && (
              <NavLink to="/admin/reports" className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                <span className="text-sm font-medium text-red-700">
                  {stats.moderation.pending_reports} signalements à traiter
                </span>
                <ArrowUpRight className="w-4 h-4 text-red-500" />
              </NavLink>
            )}
            {stats?.moderation?.open_tickets > 0 && (
              <NavLink to="/admin/tickets" className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <span className="text-sm font-medium text-blue-700">
                  {stats.moderation.open_tickets} tickets support ouverts
                </span>
                <ArrowUpRight className="w-4 h-4 text-blue-500" />
              </NavLink>
            )}
            {stats?.moderation?.pending_validations === 0 && stats?.moderation?.pending_reports === 0 && stats?.moderation?.open_tickets === 0 && (
              <div className="text-center py-6 text-gray-500">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                Tout est à jour!
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Revenus
          </h3>
          <div className="text-center py-6">
            <span className="text-4xl font-bold text-gray-900">{stats?.revenue?.total?.toLocaleString() || 0}</span>
            <span className="text-lg text-gray-500 ml-2">{stats?.revenue?.currency || 'TND'}</span>
            <p className="text-sm text-gray-500 mt-2">Revenus totaux plateforme</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Users Management Component
function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', user: null });

  useEffect(() => {
    fetchUsers();
  }, [search, roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Erreur de chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async (userId, reason) => {
    try {
      await api.post(`/admin/users/${userId}/ban`, { reason });
      toast.success('Utilisateur banni');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors du bannissement');
    }
    setActionDialog({ open: false, type: '', user: null });
  };

  const handleUnbanUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unban`);
      toast.success('Utilisateur débanni');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors du débannissement');
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/verify`);
      toast.success('Utilisateur vérifié');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleAddBarka = async (userId, amount, reason) => {
    try {
      await api.post(`/admin/users/${userId}/add-barka`, { amount: parseInt(amount), reason });
      toast.success(`${amount} BARKA ajoutés`);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de BARKA');
    }
    setActionDialog({ open: false, type: '', user: null });
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}`, { role: newRole });
      toast.success('Rôle modifié');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors du changement de rôle');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <h1 className="font-fraunces text-2xl font-bold text-gray-900">Gestion Utilisateurs</h1>
        <div className="flex gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tous les rôles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="user">Utilisateur</SelectItem>
              <SelectItem value="creator">Créateur</SelectItem>
              <SelectItem value="moderator">Modérateur</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">BARKA</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.profile_picture} />
                        <AvatarFallback className="bg-[#1E4ED8] text-white">
                          {user.username?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-gray-900">{user.display_name || user.username}</p>
                        <p className="text-sm text-gray-500">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <Select value={user.role} onValueChange={(v) => handleChangeRole(user.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Utilisateur</SelectItem>
                        <SelectItem value="moderator">Modérateur</SelectItem>
                        <SelectItem value="content_admin">Admin Contenu</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-bold text-[#D9381E]">{user.barka || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {user.is_banned && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">Banni</span>
                      )}
                      {user.is_verified && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Vérifié</span>
                      )}
                      {user.is_creator && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Créateur</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(`/u/${user.username}`, '_blank')}>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir Profil
                        </DropdownMenuItem>
                        {!user.is_verified && (
                          <DropdownMenuItem onClick={() => handleVerifyUser(user.id)}>
                            <Check className="w-4 h-4 mr-2" />
                            Vérifier
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setActionDialog({ open: true, type: 'barka', user })}>
                          <Award className="w-4 h-4 mr-2" />
                          Ajouter BARKA
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.is_banned ? (
                          <DropdownMenuItem onClick={() => handleUnbanUser(user.id)}>
                            <Check className="w-4 h-4 mr-2" />
                            Débannir
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem 
                            onClick={() => setActionDialog({ open: true, type: 'ban', user })}
                            className="text-red-600"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Bannir
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ban Dialog */}
      <Dialog open={actionDialog.open && actionDialog.type === 'ban'} onOpenChange={(o) => !o && setActionDialog({ open: false, type: '', user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bannir @{actionDialog.user?.username}</DialogTitle>
            <DialogDescription>
              Cette action empêchera l'utilisateur de se connecter.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const reason = e.target.reason.value;
            handleBanUser(actionDialog.user?.id, reason);
          }}>
            <Input name="reason" placeholder="Raison du bannissement" className="mb-4" required />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setActionDialog({ open: false, type: '', user: null })}>
                Annuler
              </Button>
              <Button type="submit" variant="destructive">Bannir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add BARKA Dialog */}
      <Dialog open={actionDialog.open && actionDialog.type === 'barka'} onOpenChange={(o) => !o && setActionDialog({ open: false, type: '', user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter BARKA à @{actionDialog.user?.username}</DialogTitle>
            <DialogDescription>
              BARKA actuel: {actionDialog.user?.barka || 0}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const amount = e.target.amount.value;
            const reason = e.target.reason.value;
            handleAddBarka(actionDialog.user?.id, amount, reason);
          }}>
            <div className="space-y-4">
              <Input name="amount" type="number" placeholder="Montant" required />
              <Input name="reason" placeholder="Raison (optionnel)" />
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setActionDialog({ open: false, type: '', user: null })}>
                Annuler
              </Button>
              <Button type="submit">Ajouter BARKA</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Validations Management Component
function ValidationsManagement() {
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchValidations();
  }, []);

  const fetchValidations = async () => {
    try {
      const response = await api.get('/admin/validations');
      setValidations(response.data.validations);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (cookId) => {
    try {
      await api.post(`/admin/validations/${cookId}/approve`);
      toast.success('Validation approuvée');
      fetchValidations();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReject = async (cookId, reason) => {
    try {
      await api.post(`/admin/validations/${cookId}/reject`, { reason });
      toast.success('Validation rejetée');
      fetchValidations();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="font-fraunces text-2xl font-bold text-gray-900">Validations en Attente</h1>

      {validations.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-500">Aucune validation en attente</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {validations.map((v) => (
            <div key={v.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {v.proof_image_url && (
                <img src={v.proof_image_url} alt="Preuve" className="w-full h-48 object-cover" />
              )}
              <div className="p-4">
                <p className="font-medium text-gray-900">{v.recipe?.title}</p>
                <p className="text-sm text-gray-500">Par @{v.user?.username}</p>
                <p className="text-xs text-gray-400 mt-1">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {new Date(v.created_at).toLocaleDateString('fr-FR')}
                </p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleApprove(v.id)}>
                    <Check className="w-4 h-4 mr-1" />
                    Approuver
                  </Button>
                  <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleReject(v.id, 'Photo non valide')}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Rejeter
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Support Tickets Component
function TicketsManagement() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/admin/tickets');
      setTickets(response.data.tickets);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (ticketId, status, response) => {
    try {
      await api.put(`/admin/tickets/${ticketId}`, { status, response });
      toast.success('Ticket mis à jour');
      fetchTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-fraunces text-2xl font-bold text-gray-900">Tickets Support</h1>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {tickets.map((ticket) => (
            <div key={ticket.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedTicket(ticket)}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{ticket.subject}</p>
                  <p className="text-sm text-gray-500 truncate max-w-md">{ticket.message}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                  ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {ticket.status}
                </span>
              </div>
            </div>
          ))}
          {tickets.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              Aucun ticket
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedTicket} onOpenChange={(o) => !o && setSelectedTicket(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTicket?.subject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{selectedTicket?.message}</p>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const response = e.target.response.value;
              handleRespond(selectedTicket?.id, 'resolved', response);
            }}>
              <textarea 
                name="response"
                placeholder="Votre réponse..."
                className="w-full p-3 border border-gray-200 rounded-lg resize-none h-24"
                required
              />
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setSelectedTicket(null)}>
                  Fermer
                </Button>
                <Button type="submit">Répondre et Résoudre</Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Platform Settings Component
function PlatformSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/config');
      setConfig(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/config', config);
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
        <Shield className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-yellow-700 font-medium">Accès Super Admin requis</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="font-fraunces text-2xl font-bold text-gray-900">Configuration Plateforme</h1>

      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Commission plateforme (%)</label>
            <Input 
              type="number" 
              step="0.01"
              value={(config?.platform_commission || 0) * 100}
              onChange={(e) => setConfig({ ...config, platform_commission: parseFloat(e.target.value) / 100 })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Retrait minimum (TND)</label>
            <Input 
              type="number"
              value={config?.min_withdrawal || 50}
              onChange={(e) => setConfig({ ...config, min_withdrawal: parseFloat(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">BARKA par recette</label>
            <Input 
              type="number"
              value={config?.barka_per_recipe || 50}
              onChange={(e) => setConfig({ ...config, barka_per_recipe: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Votes requis pour validation</label>
            <Input 
              type="number"
              value={config?.validation_votes_required || 3}
              onChange={(e) => setConfig({ ...config, validation_votes_required: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Durée stories (heures)</label>
            <Input 
              type="number"
              value={config?.story_duration_hours || 24}
              onChange={(e) => setConfig({ ...config, story_duration_hours: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <Button type="submit" className="bg-[#1E4ED8]">
            Sauvegarder
          </Button>
        </div>
      </form>
    </div>
  );
}

// Analytics Dashboard Component
function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/admin/analytics?period=${period}`);
      setAnalytics(response.data);
    } catch (error) {
      // Fallback to mock data if API not available
      setAnalytics({
        users: { total: 156, new_this_period: 23, active: 89, growth: 14.7 },
        recipes: { total: 342, new_this_period: 45, validated: 298, growth: 8.2 },
        challenges: { total: 12, active: 3, participants: 234, completed: 9 },
        engagement: { 
          daily_active_users: [45, 52, 48, 61, 55, 72, 68],
          posts_per_day: [12, 15, 9, 18, 14, 21, 16],
          labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
        },
        revenue: { total: 2450, this_period: 580, tips: 320, subscriptions: 890, bookings: 1240 },
        top_regions: [
          { name: 'Tunis', users: 45, recipes: 89 },
          { name: 'Sfax', users: 32, recipes: 67 },
          { name: 'Sousse', users: 28, recipes: 54 },
          { name: 'Kairouan', users: 19, recipes: 41 },
          { name: 'Nabeul', users: 15, recipes: 32 },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const BarChart = ({ data, labels, color = '#1E4ED8', height = 120 }) => {
    const max = Math.max(...data);
    return (
      <div className="flex items-end justify-between gap-2" style={{ height }}>
        {data.map((value, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full rounded-t transition-all hover:opacity-80"
              style={{ 
                height: `${(value / max) * 100}%`,
                backgroundColor: color,
                minHeight: 4
              }}
            />
            <span className="text-xs text-gray-500">{labels[i]}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-fraunces text-2xl font-bold text-gray-900">Analytics</h1>
        <select 
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm"
        >
          <option value="day">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="year">Cette année</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-500">Utilisateurs</span>
          </div>
          <p className="text-2xl font-bold">{analytics?.users?.total}</p>
          <p className="text-xs text-green-600">+{analytics?.users?.new_this_period} cette période</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-gray-500">Recettes</span>
          </div>
          <p className="text-2xl font-bold">{analytics?.recipes?.total}</p>
          <p className="text-xs text-green-600">+{analytics?.recipes?.new_this_period} cette période</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-500">Challenges</span>
          </div>
          <p className="text-2xl font-bold">{analytics?.challenges?.active}</p>
          <p className="text-xs text-gray-500">{analytics?.challenges?.participants} participants</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-500">Revenus</span>
          </div>
          <p className="text-2xl font-bold">{analytics?.revenue?.this_period} TND</p>
          <p className="text-xs text-gray-500">Total: {analytics?.revenue?.total} TND</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Utilisateurs Actifs par Jour</h3>
          <BarChart 
            data={analytics?.engagement?.daily_active_users || []} 
            labels={analytics?.engagement?.labels || []}
            color="#1E4ED8"
          />
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Publications par Jour</h3>
          <BarChart 
            data={analytics?.engagement?.posts_per_day || []} 
            labels={analytics?.engagement?.labels || []}
            color="#E07A5F"
          />
        </div>
      </div>

      {/* Revenue Breakdown & Top Regions */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Répartition des Revenus</h3>
          <div className="space-y-3">
            {[
              { label: 'Réservations chefs', value: analytics?.revenue?.bookings, color: 'bg-blue-500' },
              { label: 'Abonnements créateurs', value: analytics?.revenue?.subscriptions, color: 'bg-purple-500' },
              { label: 'Tips', value: analytics?.revenue?.tips, color: 'bg-green-500' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${item.color}`} />
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <span className="font-medium">{item.value} TND</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Top Régions</h3>
          <div className="space-y-3">
            {analytics?.top_regions?.map((region, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' :
                    i === 1 ? 'bg-gray-100 text-gray-700' :
                    i === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-50 text-blue-600'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium">{region.name}</span>
                </div>
                <div className="text-sm text-gray-500">
                  {region.users} users • {region.recipes} recettes
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reports Management Component
function ReportsManagement() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      const response = await api.get(`/reports/admin/list?status=${statusFilter}`);
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error('Erreur de chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch report stats');
    }
  };

  const handleResolve = async () => {
    if (!selectedReport || !actionType) return;

    try {
      await api.post(`/reports/admin/${selectedReport.id}/resolve`, {
        action: actionType,
        notes: actionNotes
      });
      toast.success('Signalement traité');
      setActionDialog(false);
      setSelectedReport(null);
      setActionType('');
      setActionNotes('');
      fetchReports();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDismiss = async (reportId) => {
    if (!window.confirm('Rejeter ce signalement?')) return;
    
    try {
      await api.post(`/reports/admin/${reportId}/dismiss`, { reason: 'Signalement non valide' });
      toast.success('Signalement rejeté');
      fetchReports();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      spam: 'Spam',
      inappropriate: 'Contenu inapproprié',
      harassment: 'Harcèlement',
      misinformation: 'Fausses infos',
      hate_speech: 'Discours haineux',
      violence: 'Violence',
      nudity: 'Nudité',
      scam: 'Arnaque',
      copyright: 'Droits d\'auteur',
      other: 'Autre'
    };
    return reasons[reason] || reason;
  };

  const getTargetTypeLabel = (type) => {
    const types = {
      user: 'Utilisateur',
      post: 'Publication',
      recipe: 'Recette',
      comment: 'Commentaire',
      video: 'Vidéo',
      stream_message: 'Message live',
      message: 'Message privé'
    };
    return types[type] || type;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-[#1E4ED8] border-t-transparent rounded-full" />
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-fraunces text-2xl font-bold text-gray-900">Signalements</h1>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-500">En attente</p>
            <p className="text-2xl font-bold text-red-600">{stats.by_status?.pending || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-500">En cours</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.by_status?.reviewing || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-500">Résolus</p>
            <p className="text-2xl font-bold text-green-600">{stats.by_status?.resolved || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-gray-500">Rejetés</p>
            <p className="text-2xl font-bold text-gray-600">{stats.by_status?.dismissed || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {['pending', 'reviewing', 'resolved', 'dismissed', 'all'].map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
            className={statusFilter === status ? 'bg-[#1E4ED8]' : ''}
          >
            {status === 'pending' ? 'En attente' :
             status === 'reviewing' ? 'En cours' :
             status === 'resolved' ? 'Résolus' :
             status === 'dismissed' ? 'Rejetés' : 'Tous'}
          </Button>
        ))}
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {reports.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Flag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun signalement {statusFilter !== 'all' ? `"${statusFilter}"` : ''}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {reports.map((report) => (
              <div key={report.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-red-50">
                      <Flag className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{getTargetTypeLabel(report.target_type)}</span>
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                          {getReasonLabel(report.reason)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        Signalé par: <span className="text-gray-700">{report.reporter?.display_name || report.reporter?.username}</span>
                        {report.target_user && (
                          <> • Contre: <span className="text-gray-700">{report.target_user.display_name || report.target_user.username}</span></>
                        )}
                      </p>
                      {report.description && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{report.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(report.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  {report.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDismiss(report.id)}
                      >
                        Rejeter
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#1E4ED8]"
                        onClick={() => {
                          setSelectedReport(report);
                          setActionDialog(true);
                        }}
                      >
                        Traiter
                      </Button>
                    </div>
                  )}
                  
                  {report.status === 'resolved' && (
                    <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                      Action: {report.action_taken}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog} onOpenChange={setActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Traiter le signalement</DialogTitle>
            <DialogDescription>
              Choisis l'action à prendre pour ce signalement.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Action</label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionne une action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_action">Aucune action</SelectItem>
                  <SelectItem value="warning">Avertissement</SelectItem>
                  <SelectItem value="content_removed">Supprimer contenu</SelectItem>
                  <SelectItem value="temp_ban">Ban temporaire</SelectItem>
                  <SelectItem value="perm_ban">Ban permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Notes (optionnel)</label>
              <Input
                placeholder="Notes sur la décision..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(false)}>Annuler</Button>
            <Button onClick={handleResolve} disabled={!actionType} className="bg-[#1E4ED8]">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Admin Dashboard Component
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users' },
    { icon: Flag, label: 'Signalements', path: '/admin/reports' },
    { icon: CheckSquare, label: 'Validations', path: '/admin/validations' },
    { icon: Ticket, label: 'Tickets', path: '/admin/tickets' },
    { icon: TrendingUp, label: 'Analytics', path: '/admin/analytics' },
    { icon: Settings, label: 'Configuration', path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-[#F8F5F2]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#1E4ED8]" />
              <span className="font-fraunces font-bold text-xl">Admin MHARHER</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Connecté en tant que</span>
            <span className="font-medium text-[#1E4ED8]">{user?.username}</span>
          </div>
        </div>
      </header>

      <div className="flex max-w-7xl mx-auto">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-100 min-h-[calc(100vh-73px)] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/admin'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive
                      ? 'bg-[#1E4ED8] text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="reports" element={<ReportsManagement />} />
            <Route path="validations" element={<ValidationsManagement />} />
            <Route path="tickets" element={<TicketsManagement />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="settings" element={<PlatformSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
